import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { API_BASE, errorMessage } from "@/lib/api";

let counter = 0;

export function useUploader() {
  const [items, setItems] = useState([]);
  const controllers = useRef({});

  const update = useCallback((id, patch) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it))), []);

  const addFiles = useCallback((files) => {
    const list = Array.from(files).map((file) => ({
      id: `u${++counter}`, file, name: file.name, size: file.size, progress: 0, loaded: 0, speed: 0, eta: null, status: "pending", error: null, result: null,
      thumb: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setItems((prev) => [...prev, ...list]);
    return list;
  }, []);

  const remove = useCallback((id) => {
    controllers.current[id]?.abort();
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const cancel = useCallback((id) => {
    controllers.current[id]?.abort();
  }, []);

  const upload = useCallback(async (item, expiration) => {
    const controller = new AbortController();
    controllers.current[item.id] = controller;
    update(item.id, { status: "uploading", progress: 0, error: null });
    const form = new FormData();
    form.append("file", item.file);
    form.append("expiration", expiration);
    form.append("declared_size", String(item.size));
    const started = Date.now();
    try {
      const { data } = await axios.post(`${API_BASE}/uploads`, form, {
        withCredentials: true, signal: controller.signal, timeout: 0,
        onUploadProgress: (e) => {
          const elapsed = (Date.now() - started) / 1000;
          const speed = elapsed > 0 ? e.loaded / elapsed : 0;
          const total = e.total || item.size;
          update(item.id, { progress: Math.min(99, Math.round((e.loaded / total) * 100)), loaded: e.loaded, speed, eta: speed ? (total - e.loaded) / speed : null });
        },
      });
      update(item.id, { status: "done", progress: 100, result: data });
      return data;
    } catch (err) {
      if (axios.isCancel(err) || err.code === "ERR_CANCELED") {
        update(item.id, { status: "pending", progress: 0, error: null });
        return null;
      }
      update(item.id, { status: "error", error: errorMessage(err, "Upload failed. Please try again.") });
      return null;
    } finally {
      delete controllers.current[item.id];
    }
  }, [update]);

  const reset = useCallback(() => {
    Object.values(controllers.current).forEach((c) => c.abort());
    setItems([]);
  }, []);

  return { items, addFiles, remove, cancel, upload, reset };
}
